import { setIframeUserIdCookie } from 'utils/cookie';
import {
    getDomainOrigin,
    getAxiosConfig,
    getHeaders,
} from 'utils/serverConfig';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
    IFetchGSTDetailsResponse,
    IFinancialDetailsFormResponse,
    IFinancialDetailsSubmitRequest,
    IFinancialDetailsSubmitResponse,
    ISubmitGSTDetailsRequest,
    ISubmitGSTDetailsResponse,
    ValidateGSTDetailsResponse,
} from './types';
import API_ENDPOINTS from './url';

export async function getTrackerDetails(
    policyEkey: string,
    claimRequestNumber?: string
) {
    setIframeUserIdCookie();
    const url = policyEkey
        ? `${getDomainOrigin()}/partnerships/fetch/tracker?policy_ekey=${policyEkey}`
        : `${getDomainOrigin()}/partnerships/fetch/tracker?claim_request_number=${claimRequestNumber}`;
    const response = await axios.get(url);
    if (response?.data) {
        return response.data;
    }
    return null;
}

export const sendR2D2Event = ({
    ekind,
    okind = 'electronics_fnol',
    edata,
    odata,
    trackerDetails,
    policyDetails,
}) => {
    return r2d2({
        ekind,
        edata: {
            plan: policyDetails?.plan?.name,
            plan_type: policyDetails?.plan?.category,
            partner: policyDetails?.partner?.id,
            policy_number: policyDetails?.policy_number,
            product_name: policyDetails?.product?.name,
            category: policyDetails?.product?.category,
            serial_number: policyDetails?.product?.serial_number,
            ...edata,
        },
        odata: {
            user_id: Cookies.get('user_id') || '',
            email: policyDetails?.customer?.email,
            phone: policyDetails?.customer?.phone,
            platform: window.innerWidth < 480 ? 'mweb' : 'web',
            event_type: odata?.eventType,
            lob: odata?.lob,
            page_url: window.location.href,
            refer_url: policyDetails?.referrer_url,
            ip: trackerDetails?.ip,
            device: trackerDetails?.browser,
        },
        okind,
        oid: new URL(window.location.href)?.searchParams?.get('policyid')!,
    }).catch(() => null);
};

export const uploadDocument = async (
    url: string,
    file: File,
    documentType: string,
    user_id: string,
    onUploadProgress: any
) => {
    try {
        const myHeaders = new Headers();
        myHeaders.append('accept', 'application/json, text/plain, */*');
        myHeaders.append('content-type', 'application/json;charset=UTF-8');

        const formData = new FormData();
        formData.append('document', file, file.name);
        formData.append('requestContext', 'POLICY');
        formData.append('documentType', documentType);
        formData.append('userId', user_id);

        const { status, data } = await axios.request({
            method: 'POST',
            url,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress,
        });
        if (status === 200) {
            return {
                status: true,
                data,
            };
        }
        return {
            status: false,
        };
    } catch (e: any) {
        if (e?.response?.status === 401) {
            return {
                statusCode: 401,
            };
        }
        return {
            status: false,
        };
    }
};

export const callValidateGSTDetails = async (
    partnerId: string,
    groupName: string,
    async: boolean
): Promise<ValidateGSTDetailsResponse> => {
    try {
        const { data } = await axios({
            method: 'POST',
            url: API_ENDPOINTS.gst.validateGSTDetails(
                partnerId,
                groupName,
                async
            ),
            ...getAxiosConfig(),
        });

        return {
            is_error: false,
            data,
            error_message: '',
        };
    } catch (error) {
        return {
            is_error: true,
            error_message: '',
            data: null,
        };
    }
};

export const fetchCurrentGSTDetailsOfUser = async (
    partnerId: string,
    groupName: string
): Promise<IFetchGSTDetailsResponse> => {
    try {
        const { data } = await axios({
            method: 'GET',
            url: API_ENDPOINTS.gst.fetchGSTDetails(partnerId, groupName),
            ...getAxiosConfig(),
        });

        return {
            is_error: false,
            data,
            error_message: '',
        };
    } catch (error) {
        return {
            is_error: true,
            error_message: '',
            data: null,
        };
    }
};

export const validateGSTDetailsOfUser = async (
    partnerId: string,
    groupName: string
): Promise<IFetchGSTDetailsResponse> => {
    try {
        const { data } = await axios({
            method: 'GET',
            url: API_ENDPOINTS.gst.fetchGSTDetails(partnerId, groupName),
            ...getAxiosConfig(),
        });

        return {
            is_error: false,
            data,
            error_message: '',
        };
    } catch (error) {
        return {
            is_error: true,
            error_message: '',
            data: null,
        };
    }
};

export const submitGSTDetailsOfUser = async (
    partnerId: string,
    groupName: string,
    request: ISubmitGSTDetailsRequest
): Promise<ISubmitGSTDetailsResponse> => {
    try {
        const { data } = await axios({
            method: 'POST',
            url: API_ENDPOINTS.gst.submitGSTDetails(partnerId, groupName),
            ...getAxiosConfig(),
            data: request,
        });

        return {
            is_error: false,
            data,
            error_message: '',
        };
    } catch (error) {
        return {
            is_error: true,
            error_message: '',
            data: null,
        };
    }
};

export const fetchFinancialDetailsForm = async (
    partnerId: string,
    groupName: string
): Promise<{
    is_error: boolean;
    data: IFinancialDetailsFormResponse | null;
    error_message: string;
    statusCode?: number;
}> => {
    try {
        const { data, status } = await axios({
            method: 'GET',
            url: API_ENDPOINTS.financialDetails.form(partnerId, groupName),
            ...getAxiosConfig(),
        });
        if (status === 200 && data) {
            return {
                is_error: false,
                data,
                error_message: '',
            };
        }
        return {
            is_error: true,
            data: null,
            error_message: 'Failed to load financial details',
        };
    } catch (error: any) {
        return {
            is_error: true,
            data: null,
            error_message:
                error?.response?.data?.message ||
                'Failed to load financial details',
            statusCode: error?.response?.status,
        };
    }
};

export const submitFinancialDetails = async (
    partnerId: string,
    groupName: string,
    request: IFinancialDetailsSubmitRequest
): Promise<{
    is_error: boolean;
    data: IFinancialDetailsSubmitResponse | null;
    error_message: string;
    statusCode?: number;
}> => {
    try {
        const { data, status } = await axios({
            method: 'POST',
            url: API_ENDPOINTS.financialDetails.submit(partnerId, groupName),
            data: request,
            ...getAxiosConfig(),
        });
        if (status === 200 && data) {
            return {
                is_error: false,
                data,
                error_message: '',
            };
        }
        return {
            is_error: true,
            data: null,
            error_message: 'Failed to submit financial details',
        };
    } catch (error: any) {
        return {
            is_error: true,
            data: error?.response?.data || null,
            error_message:
                error?.response?.data?.message || 'Oops. something went wrong.',
            statusCode: error?.response?.status,
        };
    }
};

export const dealerPayment = async (
    partnerId: string,
    order_ekey: string | string[] | undefined
) => {
    try {
        const { status, data } = await axios({
            method: 'POST',
            url: `${getDomainOrigin()}/api/mpos/partner/${partnerId}/payment`,
            data: {
                user_type: 'dealer',
                order_ekey,
            },
            headers: {
                ...getHeaders(),
            },
        });
        if (status === 200 && data) {
            return {
                data,
                status: true,
            };
        }
        return {
            status: false,
        };
    } catch (e: any) {
        if (e?.response?.status === 401) {
            return {
                statusCode: 401,
                status: false,
            };
        }
        return {
            status: false,
        };
    }
};
